import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Trash2, Search, Tag, MapPin, Mail, User, ShoppingBag, Eye } from 'lucide-react';

const AdminClassifieds = () => {
  const [items, setItems] = useState([]);
  const [lfItems, setLfItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [subTab, setSubTab] = useState('forsale'); // 'forsale' | 'lostfound'
  const [message, setMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchClassifieds = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const res = await axiosInstance.get('/admin/classifieds', { params });
      setItems(res.data.items);
    } catch (err) { console.error(err); }
  };

  const fetchLostFound = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const res = await axiosInstance.get('/admin/lost-found', { params });
      setLfItems(res.data.items);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchClassifieds(), fetchLostFound()]).finally(() => setLoading(false));
  }, []);

  const handleSearch = () => {
    setLoading(true);
    Promise.all([fetchClassifieds(), fetchLostFound()]).finally(() => setLoading(false));
  };

  const handleDelete = async (type, id) => {
    try {
      const endpoint = type === 'forsale' ? `/admin/classifieds/${id}` : `/admin/lost-found/${id}`;
      await axiosInstance.delete(endpoint);
      setMessage('Deleted successfully');
      if (type === 'forsale') setItems(prev => prev.filter(i => i.id !== id));
      else setLfItems(prev => prev.filter(i => i.id !== id));
      setConfirmDelete(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Failed to delete');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axiosInstance.put(`/admin/classifieds/${id}/status?status=${newStatus}`);
      setItems(prev => prev.map(i => i.id === id ? { ...i, status: newStatus } : i));
      setMessage(`Status changed to ${newStatus}`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Failed to update status');
    }
  };

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    pending_collection: 'bg-amber-100 text-amber-700',
    sold: 'bg-red-100 text-red-700',
    expired: 'bg-gray-100 text-gray-500',
    resolved: 'bg-blue-100 text-blue-700',
  };

  const statusLabels = {
    active: 'Available',
    pending_collection: 'Pending Collection',
    sold: 'Sold',
    expired: 'Expired',
    resolved: 'Resolved',
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div className="bg-white rounded-lg shadow" data-testid="admin-classifieds-panel">
      {/* Header */}
      <div className="p-4 border-b flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Classifieds & Lost/Found</h2>
          <p className="text-sm text-gray-500">{items.length} Sell items &middot; {lfItems.length} Lost & Found items</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by title, name, or email..."
            className="flex-1 sm:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-400"
            data-testid="admin-classifieds-search"
          />
          <button onClick={handleSearch} className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm" data-testid="admin-classifieds-search-btn">
            <Search className="h-4 w-4" />
          </button>
        </div>
      </div>

      {message && (
        <div className="mx-4 mt-3 px-3 py-2 bg-emerald-50 text-emerald-700 text-sm rounded-lg">{message}</div>
      )}

      {/* Sub-tabs */}
      <div className="flex border-b">
        <button
          onClick={() => setSubTab('forsale')}
          className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
            subTab === 'forsale' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500'
          }`}
          data-testid="admin-tab-forsale"
        >
          <ShoppingBag className="h-4 w-4 inline mr-1" /> Sell ({items.length})
        </button>
        <button
          onClick={() => setSubTab('lostfound')}
          className={`flex-1 py-3 text-sm font-medium text-center border-b-2 transition-colors ${
            subTab === 'lostfound' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500'
          }`}
          data-testid="admin-tab-lostfound"
        >
          <Eye className="h-4 w-4 inline mr-1" /> Lost & Found ({lfItems.length})
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="p-8 text-center text-gray-400">Loading...</div>
      ) : (
        <div className="divide-y">
          {subTab === 'forsale' ? (
            items.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No Sell listings found</div>
            ) : (
              items.map(item => (
                <div key={item.id} className="p-4 hover:bg-gray-50" data-testid={`admin-classified-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 truncate">{item.title}</h3>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${statusColors[item.status] || 'bg-gray-100'}`}>
                          {statusLabels[item.status] || item.status}
                        </span>
                        {item.price !== null && <span className="text-sm font-bold text-gray-700">€{item.price}</span>}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" />{item.category}</span>
                        <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{item.contactName}</span>
                        <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{item.contactEmail}</span>
                        {item.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{item.location}</span>}
                        <span>{formatDate(item.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.status === 'active' && (
                        <select
                          onChange={(e) => e.target.value && handleStatusChange(item.id, e.target.value)}
                          className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                          defaultValue=""
                          data-testid={`admin-status-select-${item.id}`}
                        >
                          <option value="" disabled>Status...</option>
                          <option value="pending_collection">Pending</option>
                          <option value="sold">Sold</option>
                          <option value="expired">Expire</option>
                        </select>
                      )}
                      <button
                        onClick={() => setConfirmDelete({ type: 'forsale', id: item.id, title: item.title })}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        data-testid={`admin-delete-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )
          ) : (
            lfItems.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No Lost & Found items found</div>
            ) : (
              lfItems.map(item => (
                <div key={item.id} className="p-4 hover:bg-gray-50" data-testid={`admin-lf-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full uppercase ${item.itemType === 'lost' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                          {item.itemType}
                        </span>
                        <h3 className="font-medium text-gray-900 truncate">{item.title}</h3>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${statusColors[item.status] || 'bg-gray-100'}`}>
                          {statusLabels[item.status] || item.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" />{item.category}</span>
                        <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{item.contactName}</span>
                        <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{item.contactEmail}</span>
                        {item.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{item.location}</span>}
                        <span>{formatDate(item.createdAt)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setConfirmDelete({ type: 'lostfound', id: item.id, title: item.title })}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                      data-testid={`admin-lf-delete-${item.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )
          )}
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Listing?</h3>
            <p className="text-sm text-gray-600 mb-4">Delete "<strong>{confirmDelete.title}</strong>"? This cannot be undone.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete.type, confirmDelete.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium"
                data-testid="admin-confirm-delete"
              >Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClassifieds;
